import React, { FunctionComponent, PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import { I18n } from 'react-redux-i18n';

import { RootState } from "../../store";
import * as generalActions from "../../modules/general";
import { deleteImpersonation, logOut } from "../../modules/session";
import { grecaptcha } from "../../helpers/grecaptcha";
import { useIsMobile, useAppSelector, useAppDispatch, useSession } from "../../hooks";
import General from "../../pages/general";
import { StickyNoticeBar } from '../common/stickyNoticeBar';
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { useLayoutNotices } from './useLayoutNotices';

import "./Navigation.css";

export const Navigation: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const isMobile = useIsMobile();

  const general = useAppSelector((state: RootState) => state.general);
  const session = useSession();

  const dispatch = useAppDispatch();

  const currentUserId = session?.account?.id;
  const firstName = session?.account?.firstName;
  const organization = session?.organization;

  const handleLanguageChange = (value: string) => {
    dispatch(generalActions.changeLocale(value));
  };

  const handleDeleteImpersonation = () => {
    grecaptcha(token => dispatch(deleteImpersonation(token)), "SessionOperation");
  };

  const handleLogOut = () => {
    grecaptcha(token => dispatch(logOut(token)), "SessionOperation");
  };

  const notices = useLayoutNotices({
    onStopImpersonating: handleDeleteImpersonation
  });

  // Create a unique key based on user roles and impersonation status to force re-render
  const roleKey = `${currentUserId}-${session.isSystemAdmin}-${session.impersonated}`;

  return (
    <div className="navigation">
      <General.LanguageModal
        open={general?.modals?.language}
        languages={general?.languages?.data}
        value={general?.languages?.current}
        onChange={handleLanguageChange}
        onClose={() =>
          dispatch(generalActions.toggleModal("language", !general?.modals?.language))
        }
      />

      {isMobile ? (
        <Topbar
          key={roleKey}
          location={location}
          session={session}
          organization={organization}
          currentUserId={currentUserId!}
          firstName={firstName!}
          onLogOut={handleLogOut}
          onDeleteImpersonation={handleDeleteImpersonation}
        />
      ) : (
        <Sidebar
          key={roleKey}
          location={location}
          session={session}
          organization={organization}
          currentUserId={currentUserId!}
          firstName={firstName!}
          onLogOut={handleLogOut}
          onDeleteImpersonation={handleDeleteImpersonation}
        />
      )}

      <StickyNoticeBar
        notices={notices}
        label={I18n.t('general.notice-bar.label')}
        className="navigation-notice-bar"
      />

      <div>
        {children}
      </div>
    </div>
  );
};
